import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import RenderTextfieldHeader from '../../renderTextFieldHeader';
import FeedIcon from '@mui/icons-material/Feed';
import EditIcon from '@mui/icons-material/Edit';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { colorTypes, columnTypes, dateConfig, regionalFields, renderDateCell, renderTextCell } from './columnTypes';
import { renderOcplcSelectHeader } from 'pages/ocplcDashboard/ocplcHelpers';
import { VERSION_0002 } from 'constants/ocplcConstants';


const createTextColumn = (field, label, filters, handleChange, getColor = null) => ({
    headerName: label,
    field,
    width: 155,
    sortable: false,
    headerClassName: 'dashboard-data-grid-headers',
    renderCell: (params) => renderTextCell(params.row[field], getColor ? getColor(params.row.status) : undefined),
    renderHeader: () => (
        <RenderTextfieldHeader colData={columnTypes[field]} label={label} filters={filters} handleChange={handleChange} />
    ),
});

const createDateColumn = (field, label, filters, handleChange) => ({
    headerName: label,
    field,
    width: 155,
    sortable: false,
    headerClassName: 'dashboard-data-grid-headers',
    renderCell: (params) => renderDateCell(params.row[field]),
    renderHeader: () => (
        <RenderTextfieldHeader colData={columnTypes[field]} label={label} filters={filters} handleChange={handleChange} />
    ),
});

const createSelectColumn = (field, label, filters, handleChange, dropdownData) => ({
    headerName: label,
    field,
    width: 155,
    sortable: false,
    headerClassName: 'dashboard-data-grid-headers',
    renderCell: (params) => renderTextCell(params.row[field]),
    renderHeader: () => renderOcplcSelectHeader(columnTypes[field], filters, label, handleChange, dropdownData),
});

const createCheckboxColumn = (field) => ({
    headerName: field.slice(0, 2).toUpperCase(),
    field,
    width: 40,
    sortable: false,
    headerClassName: 'dashboard-data-grid-checkbox-headers',
    renderCell: (params) => <Checkbox disabled checked={params.row[field] === true} />,
});


export const getFactoryColumns = (
    filters,
    factoryServicesData = [],
    handleChange,
    countryList,
    businessModels,
    regionDropdown,
    handleViewModel,
    region,
    disableAction,
    handleFeedDialog
) => {

    const isFieldNotEmpty = (field) =>
        factoryServicesData.length > 0 && factoryServicesData.every((row) => row[field]);

    const { startDateLabel, endDateLabel, startDateValue, endDateValue } = dateConfig[region] || dateConfig.default;
    const version = factoryServicesData?.[0]?.version;
    const dialogType = version === VERSION_0002 ? 'serviceOfferAudit' : "serviceAvailabilityAuditV3"

    const baseColumns = [
        createTextColumn('productFamily', 'Product Family', filters, handleChange),
        createTextColumn('choiceId', 'Choice ID', filters, handleChange),
        createTextColumn('material', 'Material', filters, handleChange,(status) => colorTypes[status] || 'grey'),
        createTextColumn('materialDesc', 'Material Description', filters, handleChange),
        createSelectColumn('region', 'Region', filters, handleChange, regionDropdown),
        createTextColumn('status', 'Status', filters, handleChange),
        createSelectColumn('country', 'Country ID', filters, handleChange, countryList),
        createTextColumn('countryDesc', 'Country Description', filters, handleChange),
        createSelectColumn('businessModel', 'Business Model', filters, handleChange, businessModels),
        region === 'WW' && createDateColumn('wwAutoStartDate', 'Auto Start Date', filters, handleChange),
        createDateColumn(startDateValue, startDateLabel, filters, handleChange),
        region !== 'WW' && createDateColumn('startDate', 'Start Date', filters, handleChange),
        region !== 'WW' && createDateColumn('endDate', 'End Date', filters, handleChange),
        createDateColumn(endDateValue, endDateLabel, filters, handleChange),
        region === 'WW' && createDateColumn('wwAutoEndDate', 'Auto End Date', filters, handleChange),
    ].filter(Boolean);

    const regionalDateColumns = regionalFields.flatMap(({ startField, startName, endField, endName }) => [
        isFieldNotEmpty(startName) && createDateColumn(startField, factoryServicesData?.[0]?.[startName], filters, handleChange),
        isFieldNotEmpty(endName) && createDateColumn(endField, factoryServicesData?.[0]?.[endName], filters, handleChange),
    ]).filter(Boolean);

    const authorColumn = createTextColumn('whoChanged', 'Author', filters, handleChange);

    const regionCheckBoxColumns = region === 'WW'
        ? ['apRegion', 'euRegion', 'laRegion', 'naRegion'].map(createCheckboxColumn)
        : [];

   const actionColumn = {
    headerName: '',
    field: 'actions',
    width: 180,
    sortable: false,
    renderCell: (params) => {
        const actions = [
            {
                title: 'Audit Trail - Factory Service',
                icon: <FeedIcon />,
                onClick: () => handleFeedDialog(params.row, dialogType),
            },
            {
                title: disableAction ? 'View' : 'Edit',
                icon: disableAction ? <VisibilityIcon /> : <EditIcon />,
                onClick: () => handleViewModel(params.row),
            }
        ];

        return (
            <Box onClick={(e) => e.stopPropagation()}>
                {actions.map((action, idx) => (
                    <Tooltip title={action.title} key={idx}>
                        <Button onClick={action.onClick} startIcon={action.icon} size="medium" />
                    </Tooltip>
                ))}
            </Box>
        );
    },
};


    return [...baseColumns, ...regionalDateColumns, authorColumn, ...regionCheckBoxColumns, actionColumn];
};
